import {
  type ActorJournalHead,
  type ActorSyncImportChange,
  createActorJournalHead,
  isActorAppTableName,
  readActorJournal,
} from "@-/dev.improve.haniverse.lib.database.actor.migration-tags";
import type { ActorSchemaEngine } from "#update-actor-schema";

/**
 * What a replica holds that the cloud copy may not (see `database.md`, "Device: sync supervisor
 * and rebuild"): the device's half of the import endpoint.
 *
 * The engine logs every local write in `turso_cdc` with the row's image, and clears the log once
 * an upload is in. So the log holds exactly the changes the cloud copy has not seen, and this
 * reads it with the engine's own decoders (`bin_record_json_object`, `table_columns_json_array`:
 * engine fact 13 in `database.md`). When the log cannot be read at all, every row of every app
 * table is exported instead, as an insert, which the gateway applies as create-or-revive: a row
 * the cloud already holds is left alone.
 */
export interface ActorSyncExport {
  changes: ActorSyncImportChange[];
  /** The last change id the log held, so a later export can send only what came after. */
  mark: number;
  /** `log` when the change log was read; `tables` when every row had to be sent instead. */
  source: "log" | "tables";
}

export interface ActorSyncExportOptions {
  /** Only changes logged after this id: a second export after a first one was sent. */
  afterChangeId?: number;
}

/** The engine's own change log marks: insert, update, delete, and a transaction boundary. */
const CHANGE_INSERT = 1;
const CHANGE_UPDATE = 0;
const CHANGE_DELETE = -1;

const logSql = (afterChangeId: number) =>
  "SELECT change_id, change_type, table_name, " +
  "CASE WHEN after IS NULL THEN NULL ELSE bin_record_json_object(table_columns_json_array(table_name), after) END AS after_json, " +
  "CASE WHEN before IS NULL THEN NULL ELSE bin_record_json_object(table_columns_json_array(table_name), before) END AS before_json " +
  `FROM turso_cdc WHERE table_name IS NOT NULL AND change_id > ${Math.trunc(afterChangeId)} ORDER BY change_id`;
const MARK_SQL = "SELECT coalesce(max(change_id), 0) AS mark FROM turso_cdc";

function readRows(rows: unknown[]): Record<string, unknown>[] {
  return rows.filter(
    (row): row is Record<string, unknown> => typeof row === "object" && row !== null,
  );
}

function isRow(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function readRowImage(value: unknown): Record<string, unknown> | null {
  if (typeof value !== "string") {
    return null;
  }
  try {
    const parsed: unknown = JSON.parse(value);
    return isRow(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

function quote(name: string): string {
  return `"${name.replaceAll('"', '""')}"`;
}

async function exportFromLog(
  engine: Pick<ActorSchemaEngine, "all">,
  afterChangeId: number,
): Promise<ActorSyncExport> {
  const changes: ActorSyncImportChange[] = [];
  const mark = Number(readRows(await engine.all(MARK_SQL))[0]?.["mark"] ?? 0);
  for (const row of readRows(await engine.all(logSql(afterChangeId)))) {
    const table = row["table_name"];
    const type = Number(row["change_type"]);
    if (typeof table !== "string" || !isActorAppTableName(table)) {
      continue;
    }
    if (type === CHANGE_DELETE) {
      const id = readRowImage(row["before_json"])?.["id"];
      if (typeof id === "string") {
        changes.push({ id, kind: "delete", table });
      }
      continue;
    }
    if (type !== CHANGE_INSERT && type !== CHANGE_UPDATE) {
      continue;
    }
    const after = readRowImage(row["after_json"]);
    if (after !== null) {
      changes.push({ kind: type === CHANGE_INSERT ? "insert" : "update", row: after, table });
    }
  }
  return { changes, mark: Number.isFinite(mark) ? mark : 0, source: "log" };
}

async function exportFromTables(
  engine: Pick<ActorSchemaEngine, "all">,
): Promise<ActorSyncImportChange[]> {
  const tables = readRows(
    await engine.all("SELECT name FROM sqlite_master WHERE type = 'table' ORDER BY name"),
  )
    .map((row) => row["name"])
    .filter((name): name is string => typeof name === "string" && isActorAppTableName(name));
  const changes: ActorSyncImportChange[] = [];
  for (const table of tables) {
    for (const row of readRows(await engine.all(`SELECT * FROM ${quote(table)}`))) {
      changes.push({ kind: "insert", row, table });
    }
  }
  return changes;
}

/**
 * Everything the replica holds that may be missing from the cloud copy, in the order it was
 * written. Reads the change log; falls back to the tables when the log cannot be read.
 */
export async function exportActorSyncChanges(
  engine: Pick<ActorSchemaEngine, "all">,
  { afterChangeId = -1 }: ActorSyncExportOptions = {},
): Promise<ActorSyncExport> {
  try {
    return await exportFromLog(engine, afterChangeId);
  } catch {
    const changes = await exportFromTables(engine);
    return { changes, mark: changes.length, source: "tables" };
  }
}

/** The export plus what the import needs to carry it: the replica's journal and the import's name. */
export interface ActorReplicaExport extends ActorSyncExport {
  /** Names the import: whose replica, what was read and how far. The same content gets the same name. */
  importId: string;
  /** The replica's own migration journal, which the gateway compares with the cloud copy's. */
  journalHead: ActorJournalHead;
}

/** The characters an import name may carry (see the gateway's import parser). */
const IMPORT_ID_UNSAFE = /[^\w.-]/;

/**
 * Everything a replica about to be replaced still holds for the cloud, named for the import. A
 * second export after the first was sent passes the first's `mark` as `afterChangeId` and gets a
 * new name, so the gateway applies exactly the changes in between.
 */
export async function exportActorReplica(
  engine: Pick<ActorSchemaEngine, "all">,
  {
    afterChangeId,
    kind = "rebuild",
    name,
  }: ActorSyncExportOptions & { kind?: "rebuild" | "signup"; name: string },
): Promise<ActorReplicaExport> {
  const read = async (sql: string) => readRows(await engine.all(sql));
  const journalHead = createActorJournalHead(await readActorJournal(read));
  const exported = await exportActorSyncChanges(engine, { afterChangeId });
  const who = Array.from(name, (char) => (IMPORT_ID_UNSAFE.test(char) ? "_" : char))
    .join("")
    .slice(0, 64);
  return {
    ...exported,
    importId: `${kind}:${who}:${exported.source}-${exported.mark}`,
    journalHead,
  };
}
